import {
  useWSModalCustom,
  WSInfoBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { queriesBatches } from "@query";

import { ErrorAlert } from "@components/ErrorAlert";
import { IdempotencyKey } from "../../../../v3/utils/idempotencyKey";

type Props = {
  batchId: string;
  /** Plural noun for what the batch imports, e.g. "payables". */
  entityLabelPlural: string;
  /** Sentence-case title noun, e.g. "Import payables". */
  title: string;
} & WSModalCustomProps<boolean>;

const ModalConfirmImport: React.FC<Props> = props => {
  const [processBatch, processBatchMeta] =
    queriesBatches.processBatch.useMutation();

  const onConfirm = async () => {
    const idempotencyKey = new IdempotencyKey({
      batchId: props.batchId,
      action: "process"
    });
    await processBatch(
      { batchId: props.batchId, idempotencyKey: idempotencyKey.value },
      {
        onSuccess: () => {
          idempotencyKey.destroy();
          props.onClose(true);
        }
      }
    );
  };

  return (
    <WSModal
      title={props.title}
      size="S"
      onClose={() => props.onClose(false)}
      actions={{
        alignment: "fill",
        sortButtons: false,
        buttons: [
          {
            label: "Confirm & continue",
            onClick: onConfirm,
            loading: processBatchMeta.isLoading,
            kind: "Primary"
          },
          {
            label: "Cancel",
            onClick: () => props.onClose(false),
            kind: "Tertiary"
          }
        ]
      }}
    >
      <WSList gap="XL">
        <WSText.ParagraphSm weight="book">
          {`Are you sure you want to import ${props.entityLabelPlural} from this uploaded spreadsheet?`}
        </WSText.ParagraphSm>

        <WSInfoBox>
          This action can't be rolled back, it will import all uploaded items
          from this batch until the process is complete and will create or
          update {props.entityLabelPlural} records. If import errors occur,
          please review the spreadsheet details and try again. If additional
          updates need to be made after the import, start a new batch with the
          changes.
        </WSInfoBox>

        <ErrorAlert
          context="BulkBatchStartImport"
          error={processBatchMeta.error}
        />
      </WSList>
    </WSModal>
  );
};

export const useModalConfirmImport = () => useWSModalCustom(ModalConfirmImport);
